import { ParsedUrlQuery } from 'querystring';
import { DateTime } from 'luxon';
import { usePathname, useSearchParams } from 'next/navigation';
import { createContext, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { CloseButton } from '@calm-web/design-system';

import { useAnalytics } from '@/hooks/analytics/useAnalytics';
import { useUserState } from '@/hooks/store';
import { useRouterPush } from '@/hooks/utils/useRouterPush';
import ArrowRight from 'icons/right-arrow.svg';

import { BannerConfigType, getActiveConfig } from './data';
import { BannerArrow, BannerMessaging, CloseWrapper, CTAText, StyledBanner } from './styles';
import { OfferBannerContextProps } from './types';

export function matchQuery(
	_bannerConfig: BannerConfigType,
	pathname: string,
	query: ParsedUrlQuery,
): boolean {
	if (_bannerConfig.queries) {
		const pathQueries = _bannerConfig.queries[pathname];
		if (!pathQueries) return true;

		return pathQueries.some(querySet =>
			querySet.every(({ key, value }) => {
				return query[key] === value;
			}),
		);
	}
	return true;
}

export const OfferBannerContext = createContext<OfferBannerContextProps>({
	isBannerVisible: false,
});

// Helper function to get test date override (dev mode only)
function getTestDateOverride(): DateTime | null {
	if (typeof window === 'undefined' || process.env.NODE_ENV !== 'development') {
		return null;
	}

	// Check localStorage first
	const localStorageDate = localStorage.getItem('testDate');
	if (localStorageDate) {
		const parsed = DateTime.fromISO(localStorageDate);
		if (parsed.isValid) {
			return parsed;
		}
	}

	// Check URL query parameter
	const urlParams = new URLSearchParams(window.location.search);
	const queryDate = urlParams.get('testDate');
	if (queryDate) {
		const parsed = DateTime.fromISO(queryDate);
		if (parsed.isValid) {
			return parsed;
		}
	}

	return null;
}

export function isWithinDateRange(startDate: string, endDate: string): boolean {
	const testDate = getTestDateOverride();
	const now = testDate || DateTime.now();
	const start = DateTime.fromFormat(startDate, 'MM/dd/yyyy').startOf('day');
	const end = DateTime.fromFormat(endDate, 'MM/dd/yyyy').endOf('day');
	return now >= start && now <= end;
}

export const BannerComponent: React.FC<{ bannerConfig: BannerConfigType; onClose: () => void }> = ({
	bannerConfig,
	onClose,
}) => {
	const { formatMessage } = useIntl();
	const routerPush = useRouterPush();
	const { logEvent } = useAnalytics();

	if (!bannerConfig) return null;
	return (
		<div role="region" aria-labelledby="banner-copy">
			<StyledBanner
				bannerConfig={bannerConfig}
				role="link"
				onClick={async e => {
					e.preventDefault();
					logEvent({ eventName: 'Web Banner : Clicked' });
					routerPush(bannerConfig.url, {
						coupon: bannerConfig.coupon ?? '',
						...(bannerConfig.utm_source && { utm_source: bannerConfig.utm_source ?? '' }),
					});
				}}
				href={bannerConfig.url}
			>
				{/* Empty Div for Spacing */}
				<div></div>
				<BannerMessaging>
					<span id="banner-copy">{formatMessage(bannerConfig.longCopy)}</span>
					&nbsp;
					<CTAText>{formatMessage(bannerConfig.ctaText)}</CTAText>
					<BannerArrow>
						<ArrowRight height="24px" width="24px" viewBox="0 0 24 18" />
					</BannerArrow>
				</BannerMessaging>
				<CloseWrapper>
					<CloseButton aria-label="close" color="white" onPress={onClose} />
				</CloseWrapper>
			</StyledBanner>
		</div>
	);
};

const OfferBannerProvider = ({ children }: { children?: ReactNode }) => {
	const [isBannerVisible, setIsBannerVisible] = useState(false);
	const [userClosedBanner, setUserClosedBanner] = useState(false);
	const pathname = usePathname() ?? '';
	const searchParams = useSearchParams();
	const bannerConfig = getActiveConfig(pathname);
	const user = useUserState();

	const onClose = useCallback(() => {
		setIsBannerVisible(false);
		setUserClosedBanner(true);
		bannerConfig.onClose();
	}, [bannerConfig]);

	useEffect(() => {
		const query = searchParams ? Object.fromEntries(searchParams.entries()) : {};

		if (
			bannerConfig &&
			bannerConfig.visiblePaths.includes(pathname) &&
			matchQuery(bannerConfig, pathname, query) &&
			isWithinDateRange(bannerConfig.startDate, bannerConfig.endDate) &&
			!userClosedBanner &&
			!user?.subscription?.valid
		) {
			// TODO: refactor this code to follow best practices
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setIsBannerVisible(true);
		} else {
			setIsBannerVisible(false);
		}
	}, [pathname, isBannerVisible, userClosedBanner, bannerConfig, user?.subscription?.valid, searchParams]);

	const value = useMemo(
		() => ({
			isBannerVisible,
		}),
		[isBannerVisible],
	);

	return (
		<OfferBannerContext.Provider value={value}>
			{children}
			{isBannerVisible && <BannerComponent bannerConfig={bannerConfig} onClose={onClose} />}
		</OfferBannerContext.Provider>
	);
};

export default OfferBannerProvider;
